// 🔧 pages/admin/sponsors_list.js
// ✅ Purpose: Manager/admin page to view and manage event sponsors
// ✅ Features:
// - Uses the standard Let's Go Vote responsive manager page layout
// - Shows sponsor summary cards
// - Groups sponsors by configured sponsor level
// - Displays sponsors as responsive tiles instead of a table
// - Shows sponsor logo, website, description, level, and sponsor award status
// - Links to edit each sponsor
// - Links to public sponsor_view page for each sponsor
// - Supports sponsor delete with logo cleanup
// - Shows success/error notifications using top-page colored alerts
// - Shows helpful empty-state guidance when no sponsors exist
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PromotionSourceButton from "../../promotion/components/promotion_source_button";
import { useEventContext } from "../../hooks/use_event_context";
import { db, storage, functions } from "../../firebase";
import {
  collection,
  query,
  getDocs,
  deleteDoc,
  doc,
  getDoc,
  orderBy,
} from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import { showStatus } from "../../utils/status_helpers";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";
import "react-quill/dist/quill.snow.css";
import { sanitizeHtml } from "../../utils/sanitize_html";

const SponsorList = () => {
  // 🌎 Event context
  const { eventId, eventslug, eventDoc } = useEventContext();

  // 🧠 Page state
  const [sponsors, setSponsors] = useState([]);
  const [status, setStatus] = useState(null);
  const [sponsorLevels, setSponsorLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sponsorAwardsEnabled, setSponsorAwardsEnabled] = useState(true);

  // 🔗 Route helpers
  const getSponsorEditPath = (sponsor) => `/${eventslug}/sponsor/${sponsor.sponsorSlug || sponsor.id}`;
  const getSponsorViewPath = (sponsor) => `/${eventslug}/sponsors/${sponsor.sponsorSlug || sponsor.id}`;
  const getSponsorSettingsPath = () => `/${eventslug}/sponsors_settings`;

  // =====================================================
  // 📦 Load sponsor level definitions from voting settings
  // =====================================================
  const loadLevels = async () => {
    try {
      const settingsRef = doc(db, "events", eventId, "settings", "voting");
      const snap = await getDoc(settingsRef);

      if (snap.exists()) {
        const data = snap.data();

        if (Array.isArray(data.sponsorLevels)) {
          setSponsorLevels(data.sponsorLevels.map((lvl) => lvl.name));
        }

        setSponsorAwardsEnabled(data.sponsorAwardsEnabled !== false);
      }
    } catch (err) {
      logger.warn("⚠️ Could not load sponsor levels", {
        value: err,
      });

      showStatus(setStatus, {
        type: "warning",
        message: "Unable to load sponsor levels.",
      });
    }
  };

  // =====================================================
  // 📦 Load sponsors for the event
  // =====================================================
  const fetchSponsors = async () => {
    if (!eventId) return;

    try {
      setLoading(true);

      const sponsorsQuery = query(
        collection(db, "events", eventId, "sponsors"),
        orderBy("createdAt", "desc")
      );

      const snapshot = await getDocs(sponsorsQuery);

      const results = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setSponsors(results);

      logger.log("✅ Sponsors loaded:", {
        value: results.length,
      });
    } catch (err) {
      logger.error("🔥 Error loading sponsors:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to load sponsors.",
      });
    } finally {
      setLoading(false);
    }
  };

  // 🚀 Load page data when event is ready
  useEffect(() => {
    if (eventId) {
      loadLevels();
      fetchSponsors();
    }
  }, [eventId]);

  // =====================================================
  // 🎗 Sponsors Add-on Page Gate
  // =====================================================
  // ✅ Purpose:
  // - Prevent direct URL access to sponsor pages when the Sponsors add-on is inactive
  // - Keeps public, sponsor-contact, and manager sponsor tools hidden
  // - Friendly message instead of exposing sponsor functionality
  // =====================================================
  const sponsorsEnabled =
    hasPurchasedAddOn(eventDoc, "sponsors") ||
    hasPurchasedAddOn(eventDoc, "sponsor");

  if (!sponsorsEnabled) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "900px" }}>
          <div className="alert alert-info text-center mb-0">
            Sponsors are not active for this event.
          </div>
        </div>
      </section>
    );
  }

  // 🗑️ Delete sponsor and logo
  const handleDelete = async (sponsor) => {
    const sponsorName = sponsor.name || "Unnamed Sponsor";

    if (!window.confirm(`Delete sponsor '${sponsorName}'?`)) return;

    try {
      await deleteDoc(doc(db, "events", eventId, "sponsors", sponsor.id));

      if (sponsor.logoUrl) {
        const path = new URL(sponsor.logoUrl).pathname;
        const cleanPath = decodeURIComponent(
          path.replace(/^\/v0\/b\/[^/]+\/o\//, "")
        );
        const fileRef = ref(storage, cleanPath);

        await deleteObject(fileRef);
      }

      showStatus(setStatus, {
        type: "warning",
        message: `Deleted sponsor '${sponsorName}'.`,
      });

      fetchSponsors();
    } catch (err) {
      logger.error("🔥 Delete error:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Error deleting sponsor.",
      });
    }
  };

  // ⏳ Loading state
  if (loading) {
    return <FullPageSpinner message="Loading Sponsors..." />;
  }

  // =====================================================
  // 🧩 Group sponsors by configured sponsor level
  // =====================================================
  // Sponsors without a configured level are shown in fallback "Event Sponsors".
  const levelMap = {};

  sponsorLevels.forEach((lvl) => {
    levelMap[lvl] = [];
  });

  const eventSponsors = [];

  sponsors.forEach((sponsor) => {
    if (sponsor.level && levelMap[sponsor.level]) {
      levelMap[sponsor.level].push(sponsor);
    } else {
      eventSponsors.push(sponsor);
    }
  });

  const sponsorDisplayGroups = [
    ...sponsorLevels.map((levelName) => ({
      levelName,
      sponsors: levelMap[levelName] || [],
    })),
    {
      levelName: "Event Sponsors",
      sponsors: eventSponsors,
    },
  ].filter((group) => group.sponsors.length > 0);

  // 📊 Summary counts
  const totalSponsors = sponsors.length;

    // =====================================================
    // 💳 Sponsor payment display helpers
    // Purpose:
    // - Gives managers a clear sponsor payment/approval status
    // - Keeps badge text/colors consistent across sponsor cards
    // =====================================================

    const getSponsorPaymentBadge = (sponsor) => {
      if (sponsor.paymentStatus === "paid") {
        return {
          className: "bg-success",
          label: "Paid / Approved",
        };
      }

      if (sponsor.paymentStatus === "pending") {
        return {
          className: "bg-warning text-dark",
          label: "Payment Pending",
        };
      }

      if (sponsor.paymentStatus === "not_required") {
        return {
          className: "bg-secondary",
          label: "No Payment Required",
        };
      }

      if (sponsor.approvalStatus === "pending") {
        return {
          className: "bg-warning text-dark",
          label: "Approval Pending",
        };
      }

      return {
        className: "bg-light text-dark border",
        label: "Legacy Sponsor",
      };
    };

    const pendingPaymentCount = sponsors.filter(
      (sponsor) => sponsor.paymentStatus === "pending"
    ).length;

    const paidSponsorCount = sponsors.filter(
      (sponsor) => sponsor.paymentStatus === "paid"
    ).length;

    const handleSendSponsorPaymentLink = async (sponsor) => {
      if (!eventId || !sponsor?.id) return;

      if (!sponsor.sponsorContactEmail) {
        showStatus(setStatus, {
          type: "warning",
          message: "Sponsor contact email is required before sending a payment link.",
        });

        return;
      }

      try {
        setLoading(true);

        const createSponsorCheckoutSession = httpsCallable(
          functions,
          "createSponsorCheckoutSession"
        );

        await createSponsorCheckoutSession({
          eventId,
          sponsorId: sponsor.id,
        });

        showStatus(setStatus, {
          type: "success",
          message: `Payment link emailed to ${sponsor.name || "sponsor"}.`,
        });

        await fetchSponsors();
      } catch (err) {
        logger.error("❌ Failed to send sponsor payment link:", {
          value: err,
        });

        showStatus(setStatus, {
          type: "error",
          message:
            err?.message ||
            "Failed to send sponsor payment link. Check payment settings, sponsor email, and Stripe setup.",
        });
      } finally {
        setLoading(false);
      }
    };

    const sponsorPaymentsEnabled =
      eventDoc?.paymentFeatures?.sponsors === true ||
      eventDoc?.paymentFeatures?.sponsorshipPurchases === true;

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1400px" }}>
        {/* ✅ Page Header */}
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">Sponsor List</h1>
            <p className="lead text-muted mb-0">
              Manage sponsor tiles, sponsor levels, public sponsor pages, and
              sponsor awards for this event.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex flex-wrap gap-2 justify-content-lg-end align-items-start">
            <PromotionSourceButton source="sponsors" sourceScope="collection" />
            <Link to={`/${eventslug}/sponsor`} className="btn btn-primary">
              ➕ Add Sponsor
            </Link>
          </div>
        </div>

        {/* ✅ Status Message */}
        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
            role="alert"
          >
            {status.message}
          </div>
        )}

        {/* ✅ Summary Cards */}
        <div className="row g-4 mb-4">
          <div className="col-12 col-md-6 col-xl-3">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Total Sponsors</div>
                <div className="fs-3 fw-bold">{totalSponsors}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Paid Sponsors</div>
                <div className="fs-3 fw-bold text-success">{paidSponsorCount}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Pending Payment</div>
                <div className="fs-3 fw-bold text-warning">{pendingPaymentCount}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Sponsor Levels</div>
                <div className="fs-3 fw-bold">{sponsorLevels.length}</div>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Tools Card */}
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Sponsor Tools</strong>
          </div>

          <div className="card-body">
            <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
              <div className="text-muted">
                Sponsors are grouped by sponsor level. Sponsors without a valid
                configured level appear under <strong>Event Sponsors</strong>.
              </div>

              <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                <Link
                  to={`/${eventslug}/sponsor`}
                  className="btn btn-primary"
                >
                  ➕ Add Sponsor
                </Link>

                <Link
                  to={getSponsorSettingsPath()}
                  className="btn btn-outline-secondary"
                >
                  ⚙️ Sponsor Settings
                </Link>

                <Link
                  to={`/${eventslug}/sponsors`}
                  className="btn btn-outline-primary"
                >
                  👁️ Public Sponsors
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Helpful Empty State */}
        {sponsors.length === 0 && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <h4 className="mb-3">🌟 Event Sponsors</h4>

              <p>
                Sponsors help support your event and are displayed on the public
                event page. Sponsors can include businesses, clubs,
                organizations, vendors, community partners, and event supporters.
              </p>

              {sponsorLevels.length === 0 && (
                <div className="alert alert-warning d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                  <div>
                    <strong>⚠ Sponsor Levels Not Configured</strong>
                    <br />
                    Sponsor levels help recognize major sponsors and improve the
                    public sponsor showcase.
                  </div>

                  <Link
                    to={getSponsorSettingsPath()}
                    className="btn btn-warning btn-sm"
                  >
                    ⚙️ Configure Sponsor Levels
                  </Link>
                </div>
              )}

              {sponsorLevels.length > 0 && (
                <div className="alert alert-warning d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                  <div>
                    <strong>⚠ No Sponsors Added Yet</strong>
                    <br />
                    Your sponsor levels are configured, but no sponsors have
                    been added.
                  </div>

                  <Link
                    to={`/${eventslug}/sponsor`}
                    className="btn btn-warning btn-sm"
                  >
                    ➕ Add Sponsor
                  </Link>
                </div>
              )}

              <div className="alert alert-light border">
                Sponsors without a sponsor level will automatically be grouped
                into an <strong>Event Sponsors</strong> section on the public
                sponsor page.
              </div>

              <div className="d-flex flex-wrap gap-2">
                <Link to={`/${eventslug}/sponsor`} className="btn btn-primary">
                  ➕ Add Your First Sponsor
                </Link>

                <Link
                  to={getSponsorSettingsPath()}
                  className="btn btn-outline-secondary"
                >
                  ⚙️ Sponsor Settings
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Sponsor Groups */}
        {sponsorDisplayGroups.map((group) => {
          return (
            <div key={group.levelName} className="card shadow-sm mb-4">
              <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                <div>
                  <strong>🌟 {group.levelName}</strong>
                  <div className="small text-muted">
                    {group.sponsors.length} sponsor
                    {group.sponsors.length !== 1 ? "s" : ""}
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="row g-4">
                  {group.sponsors.map((sponsor) => (
                    <div key={sponsor.id} className="col-12 col-md-6 col-xl-4">
                      <div className="card h-100 border shadow-sm">
                        <div className="card-body">
                          {/* ✅ Logo / fallback */}
                          <div
                            className="border rounded bg-light d-flex align-items-center justify-content-center mb-3"
                            style={{
                              minHeight: "120px",
                              overflow: "hidden",
                            }}
                          >
                            {sponsor.logoUrl ? (
                              <img
                                src={sponsor.logoUrl}
                                alt={`${sponsor.name || "Sponsor"} logo`}
                                className="img-fluid"
                                style={{
                                  maxHeight: "100px",
                                  maxWidth: "90%",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <span className="text-muted">No logo</span>
                            )}
                          </div>

                          {/* ✅ Sponsor heading */}
                          <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                            <div>
                              <h5 className="mb-1">
                                {sponsor.name || "Unnamed Sponsor"}
                              </h5>

                              <div className="small text-muted">
                                {sponsor.level || "Event Sponsor"}
                              </div>
                              
                              <div className="mt-2 d-flex flex-wrap gap-2">
                                {(() => {
                                  const badge = getSponsorPaymentBadge(sponsor);

                                  return (
                                    <span className={`badge ${badge.className}`}>
                                      {badge.label}
                                    </span>
                                  );
                                })()}

                                {sponsor.sponsorPaymentEmailSent === true && (
                                  <span className="badge bg-info text-dark">
                                    Email Sent
                                  </span>
                                )}
                              </div>

                            </div>
                          </div>

                          {/* ✅ Website */}
                          <div className="mb-3">
                            {sponsor.url ? (
                              <a
                                href={sponsor.url}
                                target="_blank"
                                rel="noreferrer"
                                className="small text-decoration-none"
                              >
                                🌐 Visit Website
                              </a>
                            ) : (
                              <span className="small text-muted">
                                No website
                              </span>
                            )}
                          </div>

                          {/* ✅ Description */}
                          <div className="mb-3">
                            {sponsor.description ? (
                              <div
                                className="ql-editor line-clamp-3 p-0"
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeHtml(sponsor.description),
                                }}
                              />
                            ) : (
                              <span className="text-muted">
                                No description
                              </span>
                            )}
                          </div>

                          {/* ✅ Sponsor Award */}
                          <div className="mb-3">
                            {sponsor.mapMarkerX !== null &&
                            sponsor.mapMarkerX !== undefined &&
                            sponsor.mapMarkerX !== "" &&
                            sponsor.mapMarkerY !== null &&
                            sponsor.mapMarkerY !== undefined &&
                            sponsor.mapMarkerY !== "" ? (
                              <span className="badge bg-success">
                                🗺 Map Location Added
                              </span>
                            ) : (
                              <span className="badge bg-light text-dark border">
                                No Map Location
                              </span>
                            )}
                          </div>

                          {sponsorAwardsEnabled && (
                            <div className="mb-3">
                              {sponsor.sponsorAwardTitle ? (
                                <span className="badge bg-success">
                                  🏆 {sponsor.sponsorAwardTitle}
                                </span>
                              ) : (
                                <span className="badge bg-warning text-dark">
                                  ⚠ Missing Sponsor Award
                                </span>
                              )}
                            </div>
                          )}

                          {!sponsorPaymentsEnabled && (
                            <div className="alert alert-warning mb-3">
                              Sponsorship payments are not enabled for this event. Go to Payment Settings and enable Sponsorship Payments before sending sponsor payment links.
                            </div>
                          )}

                          {/* ✅ Actions */}
                          <div className="d-flex flex-wrap gap-2">
                            <PromotionSourceButton source="sponsors" sourceId={sponsor.id} className="btn btn-sm btn-outline-primary" />
                            <Link
                              to={getSponsorViewPath(sponsor)}
                              className="btn btn-sm btn-outline-primary"
                            >
                              👁️ View
                            </Link>

                            <Link
                              to={getSponsorEditPath(sponsor)}
                              className="btn btn-sm btn-outline-secondary"
                            >
                              ✏️ Edit
                            </Link>

                              {sponsorPaymentsEnabled &&
                                sponsor.paymentRequired === true &&
                                sponsor.paymentStatus !== "paid" && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => handleSendSponsorPaymentLink(sponsor)}
                                >
                                  {sponsor.sponsorPaymentEmailSent
                                    ? "📧 Resend Payment Link"
                                    : "📧 Send Payment Link"}
                                </button>
                              )}

                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(sponsor)}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default SponsorList;
